import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { InputSearch } from '@/components/Input/InputSearch';
import Pagination from '@/components/Pagination/Pagination';
import { TableData } from '@/components/Table/TableData';
import { showErrorToast } from '@/components/toast/globalToast';
import { ShipmentStatusBadge } from '@/pages/shipments/ShipmentStatusBadge';
import {
  listSandboxShipments,
  SandboxShipmentRow,
  SandboxShipmentStatus,
} from '@/services/api/sandbox';
import { Column } from '@/types/common';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import { formatTime, statusLabel } from './sandboxUi';

const STATUS_FILTERS: SandboxShipmentStatus[] = [
  'CREATED',
  'AT_HUB',
  'DISPATCHED',
  'IN_DELIVERY',
  'FAILED',
  'COMPLETED',
  'RETURNED',
  'CANCELLED',
];

/**
 * The client's sandbox shipments, newest first — same table card as the
 * Shipments page (search, status filter, paging). A row opens its detail,
 * where the simulator buttons live.
 */
const SandboxShipmentList: React.FC<{
  canCreate: boolean;
  onCreate: () => void;
  onOpen: (id: string) => void;
  /** Bumped by the parent to reload (e.g. after a create). */
  reloadKey: number;
}> = ({ canCreate, onCreate, onOpen, reloadKey }) => {
  const [rows, setRows] = useState<SandboxShipmentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SandboxShipmentStatus | ''>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSandboxShipments({
        page,
        limit,
        status: status || undefined,
        search: search || undefined,
      });
      setRows(res.rows);
      setTotal(res.total);
    } catch (err) {
      showErrorToast(
        'Failed to load sandbox shipments',
        extractApiErrorMessage(err, 'Failed to load sandbox shipments')
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const columns: Column<SandboxShipmentRow>[] = [
    {
      key: 'waybill',
      title: 'Waybill',
      render: (_v, r) => (
        <span className="font-mono text-xs font-semibold text-primary">
          {r.waybill}
        </span>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, r) => (
        <span className="font-mono text-xs text-text-sub-600">
          {r.bookingId}
        </span>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (_v, r) => (
        <span className="text-xs text-text-sub-600">{r.type}</span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, r) => <ShipmentStatusBadge status={r.status} />,
    },
    {
      key: 'courier',
      title: 'Courier',
      render: (_v, r) => (
        <span className="text-xs text-text-sub-600">
          {r.courier?.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'itemCount',
      title: 'Items',
      render: (_v, r) => (
        <span className="text-xs tabular-nums text-text-strong-950">
          {r.itemCount}
        </span>
      ),
    },
    {
      key: 'updatedAt',
      title: 'Last update',
      render: (_v, r) => (
        <span className="text-xs tabular-nums text-text-sub-600">
          {formatTime(r.updatedAt)}
        </span>
      ),
    },
  ];

  const filtered = Boolean(status || search);

  return (
    <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
      <div className="flex flex-wrap items-center gap-3 border-b border-stroke-soft-200 p-4">
        <InputSearch
          className="w-full sm:min-w-[200px] sm:max-w-[360px] sm:flex-1"
          placeholder="Search waybill or booking ID…"
          onSearch={(v) => {
            setPage(1);
            setSearch(v.trim());
          }}
          onClear={() => {
            setPage(1);
            setSearch('');
          }}
        />
        <label className="sr-only" htmlFor="sbx-status">
          Status
        </label>
        <select
          id="sbx-status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as SandboxShipmentStatus | '');
          }}
          className="h-9 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 text-sm text-text-strong-950 focus:border-primary focus:outline-none"
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
        <div className="ml-auto">
          <Button onClick={onCreate} disabled={!canCreate}>
            <i className="ri-add-line mr-1.5" />
            New sandbox shipment
          </Button>
        </div>
      </div>

      <TableData<SandboxShipmentRow>
        data={rows}
        columns={columns}
        loading={loading}
        className="rounded-none border-0 shadow-none"
        onRowClick={(r) => onOpen(r.id)}
        renderMobileCard={(r) => (
          <div className="flex items-start justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="truncate font-mono text-xs font-semibold text-primary">
                {r.waybill}
              </p>
              <p className="truncate font-mono text-xs text-text-sub-600">
                {r.bookingId}
              </p>
              <p className="mt-1 text-xs text-text-soft-400">
                {formatTime(r.updatedAt)}
              </p>
            </div>
            <ShipmentStatusBadge status={r.status} />
          </div>
        )}
        emptyState={
          <div className="px-6 py-12 text-center">
            <i className="ri-flask-line text-2xl text-text-soft-400" />
            <p className="mt-2 text-sm font-medium text-text-strong-950">
              {filtered
                ? 'No sandbox shipments match'
                : 'No sandbox shipments yet'}
            </p>
            <p className="mt-1 text-xs text-text-sub-600">
              {filtered
                ? 'Clear the search or the status filter.'
                : 'Create one, then walk it through every status and watch its webhooks arrive.'}
            </p>
          </div>
        }
      />

      {total > 0 && (
        <div className="border-t border-stroke-soft-200 px-4 pb-4">
          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={limit}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPage(1);
              setLimit(size);
            }}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}
    </div>
  );
};

export default SandboxShipmentList;
